import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance, { authHeaders } from "../../utils/axios";

export const fetchClientConfig = createAsyncThunk(
  "brokerClients/fetchConfig",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("broker/clients/config", authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch client config");
    }
  }
);

export const fetchClients = createAsyncThunk(
  "brokerClients/fetchAll",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("broker/clients", {
        ...authHeaders(),
        params,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch clients");
    }
  }
);

export const createClient = createAsyncThunk(
  "brokerClients/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("broker/clients", data, authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to create client");
    }
  }
);

export const updateClient = createAsyncThunk(
  "brokerClients/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`broker/clients/${id}`, data, authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update client");
    }
  }
);

export const deleteClient = createAsyncThunk(
  "brokerClients/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`broker/clients/${id}`, authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete client");
    }
  }
);

export const toggleClientStatus = createAsyncThunk(
  "brokerClients/toggleStatus",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`broker/clients/${id}/status`, {}, authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to toggle client status");
    }
  }
);

const brokerClientsSlice = createSlice({
  name: "brokerClients",
  initialState: {
    clients: [],
    config: {
      person_type: [],
      profession: [],
      industry_sector: [],
      religion_community: [],
      income_group: [],
    },
    totalCount: 0,
    isLoading: false,
    isConfigLoading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Config
      .addCase(fetchClientConfig.pending, (state) => {
        state.isConfigLoading = true;
      })
      .addCase(fetchClientConfig.fulfilled, (state, action) => {
        state.isConfigLoading = false;
        state.config = action.payload.data;
      })
      .addCase(fetchClientConfig.rejected, (state, action) => {
        state.isConfigLoading = false;
      })

      // Fetch Clients
      .addCase(fetchClients.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.isLoading = false;
        // With ResponseManager.paginated, the response is { success, message, data, meta }
        const payloadData = action.payload.data;
        const payloadMeta = action.payload.meta;
        
        if (payloadMeta && payloadMeta.total !== undefined) {
          state.clients = payloadData;
          state.totalCount = payloadMeta.total;
        } else if (payloadData?.rows) {
          state.clients = payloadData.rows;
          state.totalCount = payloadData.count;
        } else {
          state.clients = Array.isArray(payloadData) ? payloadData : [];
          state.totalCount = state.clients.length;
        }
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Create Client
      .addCase(createClient.fulfilled, (state, action) => {
        // Option 1: Unshift to list (might mess up pagination)
        // Option 2: Re-fetch triggered from component (safer)
      })

      // Update Client
      .addCase(updateClient.fulfilled, (state, action) => {
        // Re-fetch handled in component
      })

      // Delete Client
      .addCase(deleteClient.fulfilled, (state, action) => {
        // Re-fetch handled in component
      })

      // Toggle Client Status
      .addCase(toggleClientStatus.fulfilled, (state, action) => {
        const index = state.clients.findIndex((c) => c.id === action.meta.arg);
        if (index !== -1 && action.payload.data) {
          state.clients[index].is_active = action.payload.data.is_active;
        }
      });
  },
});

export const { clearError } = brokerClientsSlice.actions;
export default brokerClientsSlice.reducer;
